import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Heart, Users, Building, Clock, CheckCircle2 } from 'lucide-react';

const BROOKLYN_IMAGE = "https://images.unsplash.com/photo-1635691332422-bb159df1ccc4?w=1200";

export default function OurStoryPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="bg-gradient-to-br from-slate-50 via-cyan-50/30 to-blue-50/50">
      {/* Hero */}
      <section className="py-12 md:py-16">
        <div className="container-custom text-center">
          <span className="text-sm font-semibold text-cyan-600 uppercase tracking-wider mb-4 block">Our Story</span>
          <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
            A Personal Legacy in <span className="text-slate-500">Red Hook</span>
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Founded in 2007, Nate's Pharmacy was built on the belief that healthcare should feel personal.
          </p>
        </div>
      </section>

      {/* Hero Image */}
      <section className="container-custom mb-12">
        <div className="relative h-[250px] md:h-[400px] rounded-3xl overflow-hidden">
          <img src={BROOKLYN_IMAGE} alt="Red Hook Brooklyn neighborhood and waterfront" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900/50 to-transparent"></div>
          <div className="absolute bottom-6 left-6 text-white">
            <p className="text-lg font-medium">Red Hook, Brooklyn</p>
            <p className="text-sm text-white/80">Our home since 2007</p>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-12 bg-white/50">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-10 text-center">Decades of Service</h2>

            <div className="space-y-8">
              {/* 2002 */}
              <div className="flex gap-5">
                <div className="flex flex-col items-center">
                  <div className="w-12 h-12 bg-slate-900 text-white rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0">'07</div>
                  <div className="w-0.5 h-full bg-slate-200 mt-3"></div>
                </div>
                <div className="pb-8">
                  <h3 className="text-xl font-semibold text-slate-900 mb-2">The Beginning</h3>
                  <p className="text-slate-600">Nate's Pharmacy opened at 376 Van Brunt Street, becoming a cornerstone of the Red Hook community.</p>
                </div>
              </div>

              {/* Growth */}
              <div className="flex gap-5">
                <div className="flex flex-col items-center">
                  <div className="w-12 h-12 bg-cyan-500 text-white rounded-full flex items-center justify-center flex-shrink-0">
                    <Users className="w-5 h-5" />
                  </div>
                  <div className="w-0.5 h-full bg-slate-200 mt-3"></div>
                </div>
                <div className="pb-8">
                  <h3 className="text-xl font-semibold text-slate-900 mb-2">Growing Together</h3>
                  <p className="text-slate-600">We've watched children grow up and families thrive. Our customers became family.</p>
                </div>
              </div>

              {/* Today */}
              <div className="flex gap-5">
                <div className="flex flex-col items-center">
                  <div className="w-12 h-12 bg-green-500 text-white rounded-full flex items-center justify-center flex-shrink-0">
                    <Heart className="w-5 h-5" />
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-slate-900 mb-2">Today</h3>
                  <p className="text-slate-600">While Red Hook has changed, our commitment remains. Now with free delivery across all NYC boroughs.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-12">
        <div className="container-custom">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 text-center mb-10">The Independent Difference</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <div className="bg-white p-6 rounded-2xl text-center hover:shadow-lg transition-shadow">
              <div className="w-14 h-14 bg-blue-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Heart className="w-7 h-7 text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">Personal Relationships</h3>
              <p className="text-slate-600 text-sm">We know your name, your family, and your health history.</p>
            </div>
            <div className="bg-white p-6 rounded-2xl text-center hover:shadow-lg transition-shadow">
              <div className="w-14 h-14 bg-green-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Clock className="w-7 h-7 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">Time for You</h3>
              <p className="text-slate-600 text-sm">We take time to answer questions and explain your medications.</p>
            </div>
            <div className="bg-white p-6 rounded-2xl text-center hover:shadow-lg transition-shadow">
              <div className="w-14 h-14 bg-purple-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Building className="w-7 h-7 text-purple-600" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">Community First</h3>
              <p className="text-slate-600 text-sm">We're invested in Red Hook and Brooklyn. Local support matters.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-12 bg-slate-900">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            <div>
              <div className="text-3xl md:text-4xl font-bold text-white mb-1">20+</div>
              <div className="text-slate-400 text-sm">Years in Business</div>
            </div>
            <div>
              <div className="text-3xl md:text-4xl font-bold text-white mb-1">5</div>
              <div className="text-slate-400 text-sm">NYC Boroughs</div>
            </div>
            <div>
              <div className="text-3xl md:text-4xl font-bold text-white mb-1">1000s</div>
              <div className="text-slate-400 text-sm">Happy Customers</div>
            </div>
            <div>
              <div className="text-3xl md:text-4xl font-bold text-white mb-1">FREE</div>
              <div className="text-slate-400 text-sm">Delivery Always</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12">
        <div className="container-custom text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-3">Ready to Become Part of Our Story?</h2>
          <p className="text-slate-600 mb-6">Join the families who trust Nate's Pharmacy.</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/transfer" className="bg-slate-900 text-white px-6 py-3 rounded-full font-medium hover:bg-slate-800 flex items-center gap-2">
              Transfer Prescription
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/services" className="border border-slate-300 text-slate-900 px-6 py-3 rounded-full font-medium hover:bg-slate-50">
              View Services
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
